import type { SiteSelection } from './site-preview';

/** Temporary script-disabled render surface; the visible generated preview stays opaque. */
export async function captureSiteArea(
  html: string,
  region: NonNullable<SiteSelection['region']>
) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const node of doc.querySelectorAll(
    'script,iframe,object,embed,base,form,meta[http-equiv]'
  ))
    node.remove();
  for (const node of doc.querySelectorAll('*'))
    for (const attribute of Array.from(node.attributes))
      if (attribute.name.startsWith('on') || attribute.name === 'nonce')
        node.removeAttribute(attribute.name);
  const policy = doc.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content =
    "default-src 'none'; script-src 'none'; style-src 'unsafe-inline' https://fonts.googleapis.com; img-src https: data:; font-src https: data:; base-uri 'none'; form-action 'none'; frame-src about:; connect-src 'none'";
  doc.head.prepend(policy);
  const surface = document.createElement('iframe');
  surface.setAttribute('sandbox', 'allow-same-origin');
  surface.setAttribute('aria-hidden', 'true');
  surface.tabIndex = -1;
  surface.style.cssText = `position:fixed;left:-100000px;top:0;width:${region.viewportWidth}px;height:${region.viewportHeight}px;pointer-events:none;border:0`;
  try {
    const loaded = new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(
        () => reject(new Error('Capture timed out')),
        10000
      );
      surface.onload = () => {
        window.clearTimeout(timeout);
        resolve();
      };
    });
    surface.srcdoc = `<!doctype html>${doc.documentElement.outerHTML}`;
    document.body.append(surface);
    const [html2canvas] = await Promise.all([
      import('html2canvas').then((module) => module.default),
      loaded,
    ]);
    const body = surface.contentDocument?.body;
    if (!body) throw new Error('Capture unavailable');
    const canvas = await html2canvas(body, {
      onclone: (clone) => {
        for (const animation of clone.getAnimations()) {
          try {
            animation.finish();
          } catch {
            animation.cancel();
          }
        }
      },
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale: Math.min(1, 1200 / Math.max(region.width, region.height)),
      x: region.x,
      y: region.y,
      width: region.width,
      height: region.height,
      windowWidth: region.viewportWidth,
      windowHeight: region.viewportHeight,
    });
    const screenshot = canvas.toDataURL('image/png');
    return screenshot.length <= 2000000 ? screenshot : undefined;
  } finally {
    surface.remove();
  }
}
